/**
 * OPUS ci · «Conceptos por tipo» (memoria de tipos): recordar entre proyectos qué concepto lleva cada TIPO de
 * elemento del plano. Spec: docs/_archivo/internos/spec-memoria-de-tipos.md (§3 clave y coincidencia, §4 receta,
 * §6.1 precedencia, §7 vigencia y conflicto, §8 cobertura, §10 esta lógica pura).
 *
 * Reglas que viven aquí y en ningún otro lado (la web y la API llaman a estas funciones):
 *  - La identidad del tipo sale de lo que el IFC ESTÁNDAR declara: clase canónica + nombre declarado (+ uso,
 *    + espesor si los dos lados lo declaran, + materiales cuando el modelo los trae). Nada de un programa de
 *    origen en particular.
 *  - Solo coincidencias EXACTAS: ni parecido de texto, ni vectores, ni sinónimos. Dos tipos distintos son dos
 *    tipos distintos. Ninguna función lleva listas de palabras ni tablas de unidades.
 *  - Una receta = UN concepto principal + sus complementarios. Nunca guarda precio, cantidad ni partida (v1).
 *  - Una receta con una sola línea no vigente no se aplica (ni a medias).
 */
import { z } from 'zod';
import { PLANO_INPUT_EXTRA_VALORES, seleccionesDe, type PlanoAgrupacion, type PlanoCapa, type PlanoRenglon } from './domain.js';
import type { TipoMaterialIfc } from './materialIfcDeducido.js';
import type { DerivacionDeComplementario } from './numerosGeneradores.js';
import type { MagnitudDelConcepto } from './revisionDePlano.js';

// ---------------------------------------------------------------------------------------------------
// Topes técnicos (de pantalla y de payload; no son reglas de negocio)
// ---------------------------------------------------------------------------------------------------

/** Una receta = un principal (decisión del usuario 2026-10-27). */
export const MAX_PRINCIPALES_POR_RECETA = 1;
/** Líneas (principal + complementarios) por receta. */
export const MAX_LINEAS_POR_RECETA = 40;
/** Largo máximo de una clave canónica (columna `canonica`, CHECK ≤ 600). */
export const MAX_LARGO_CANONICA = 600;
/** Materiales en la huella de una clave, y largo de cada uno. */
export const MAX_MATERIALES_EN_CLAVE = 20;
export const MAX_LARGO_MATERIAL_EN_CLAVE = 120;

// ---------------------------------------------------------------------------------------------------
// La clave del tipo (§3)
// ---------------------------------------------------------------------------------------------------

export type FormatoDePlano = 'ifc' | 'dwg';
/** De dónde salió el nombre del tipo. Solo 'tipo' y 'objectType' permiten coincidencia exacta. */
export type OrigenDelNombre = 'tipo' | 'objectType' | 'nombre' | 'clase' | 'capa' | 'bloque';

export interface ClaveDeTipo {
  formato: FormatoDePlano;
  nivel: 'clase' | 'tipo';
  /** IFC: clase canónica ('IfcWall', no 'IfcWallStandardCase'). DWG: 'capa' | 'bloque'. */
  clase: string;
  /** Nombre para MOSTRAR (espacios colapsados; DWG capa sin prefijo «XREF|»). Vacío en nivel 'clase'. */
  nombre: string;
  origenNombre: OrigenDelNombre;
  /** IFC: PredefinedType del grupo ('' = no lo declara o NOTDEFINED/USERDEFINED). DWG y nivel clase: ''. */
  uso: string;
  /** IFC: materiales que el MODELO declara y cuyo nombre sí dice material (no 'desconocido'), normalizados, sin repetir y ordenados. Huella, no clave. */
  materiales: string[];
  /** IFC: espesor del tipo en mm redondeado (`tipoDelModelo.espesorMm`); null si no lo declara. NO va en la canónica: decide en `coincidencia`. */
  espesorMm: number | null;
  /** Clave canónica (la que se indexa y se busca): `canonicaDe`. */
  canonica: string;
}

/** Lo que la memoria guardó de la clave al aprender (lo que `coincidencia` compara con el plano). */
export interface ClaveRecordada {
  formato: FormatoDePlano;
  nivel: 'clase' | 'tipo';
  origenNombre: OrigenDelNombre;
  uso: string;
  materiales: string[];
  espesorMm: number | null;
  soloSugerir: boolean;
}

/** Por qué una candidata quedó como sugerencia (se muestra traducido en la pantalla). */
export type MotivoDeSugerencia =
  | 'dwg'
  | 'clase'
  | 'solo_sugerir'
  | 'nombre_de_pieza'
  | 'uso_no_dicho'
  | 'material_distinto'
  | 'material_no_dicho';

export interface Coincidencia {
  tipo: 'exacta' | 'sugerencia' | 'no';
  /** Solo en 'sugerencia'; null en 'exacta' y 'no'. */
  motivo: MotivoDeSugerencia | null;
}

// ---------------------------------------------------------------------------------------------------
// La receta (§4)
// ---------------------------------------------------------------------------------------------------

export interface LineaDeReceta {
  /** 0..n-1, sin huecos ni repetidos. */
  orden: number;
  rol: 'principal' | 'complemento';
  /** Complemento: el `orden` del principal del que cuelga. Principal: null. */
  deOrden: number | null;
  catalogId: string;
  clave: string;
  /** Foto (la del catálogo al aprender; la escribe el servidor). */
  unidad: string | null;
  /** Foto (la del catálogo al aprender; la escribe el servidor). */
  descripcion: string;
  /** Qué se mide (principal). null = lo decide el armado como hoy (clasificar-magnitud). */
  magnitud: MagnitudDelConcepto | null;
  /** Complemento: cómo sale de su principal. null = el armado lo decide como hoy. */
  derivacion: DerivacionDeComplementario | null;
  /** Complemento: la cantidad salía de una medida supuesta → entra marcado para revisar. */
  supuesto: boolean;
}

/** Los ejes de agrupación que recuerda una receta (no los de DÓNDE está el elemento). */
export type AgrupacionDeReceta = Pick<PlanoAgrupacion, 'especialidad' | 'faseObra' | 'ordenFase' | 'sistema'>;

export interface Receta {
  /** true → lineas = [] y catalogoAncla = null. */
  noPresupuestar: boolean;
  /** catalogId de la línea principal; null ⇔ noPresupuestar. */
  catalogoAncla: string | null;
  /** `partida` siempre false en v1 (P4: ci tiene su propio sistema de partidas). */
  recuerda: { magnitud: boolean; partida: boolean; complementarios: boolean };
  /** Siempre null en v1 (P4). Se conserva el campo por compatibilidad del contrato. */
  partida: string | null;
  agrupacion: AgrupacionDeReceta | null;
  lineas: LineaDeReceta[];
}

export type ValidacionReceta = { ok: true } | { ok: false; motivo: string };

/** Lo que sale de la hoja para un elemento al pulsar «Recordar». */
export interface RecetaDesdeLaHoja {
  /** null = el elemento no tiene un principal con catálogo en la hoja. */
  receta: Receta | null;
  /** Descripciones de los renglones del elemento sin catálogo («Generado con IA: no se puede recordar»). */
  sinCatalogo: string[];
  /** Descripciones de los OTROS principales con catálogo del elemento (una receta = un principal): no se recuerdan. */
  otrosPrincipales: string[];
}

// ---------------------------------------------------------------------------------------------------
// Vigencia (§7.1)
// ---------------------------------------------------------------------------------------------------

export type EstadoDeLinea =
  | 'vigente'
  | 'catalogo_no_disponible'
  | 'catalogo_no_marcado'
  | 'no_existe'
  | 'cambio_unidad'
  | 'cambio_descripcion'
  | 'sin_precio';

// ---------------------------------------------------------------------------------------------------
// Reconocimiento, asignaciones del plano y precedencia (§6.1, §9)
// ---------------------------------------------------------------------------------------------------

/** Una receta de la memoria que coincide con un tipo del plano (respuesta de `/tipos/reconocer`). */
export interface RecetaRecordada {
  memoriaId: string;
  version: number;
  nivel: 'clase' | 'tipo';
  coincidencia: 'exacta' | 'sugerencia';
  motivo: MotivoDeSugerencia | null;
  receta: Receta;
  /** Estado de cada línea contra el catálogo y los catálogos MARCADOS del proyecto. */
  lineas: {
    orden: number;
    estado: EstadoDeLinea;
    resourceId: string | null;
    precio: number | null;
    descripcionActual: string | null;
    unidadActual: string | null;
  }[];
  /** `porOtraPersona`: nombre visible solo si la aprendió otra persona del equipo. */
  origen: { proyecto: string | null; fecha: string; porOtraPersona: string | null };
  soloSugerir: boolean;
}

export type EstadoDeReconocimiento = 'exacta' | 'sugerencia' | 'conflicto' | 'no_vigente' | 'sin_memoria';

/** Lo que devolvió `/tipos/reconocer` para UN tipo del plano. */
export interface ReconocimientoDeTipo {
  canonica: string;
  uso: string;
  estado: EstadoDeReconocimiento;
  candidatas: RecetaRecordada[];
}

/**
 * Una asignación decidida para ESTE plano (`plano_tipos_asignacion`). `clave`: en clase/tipo, la canónica (+ `uso`
 * en su campo); en pieza, 'ifc-gid|<GlobalId>' o 'dwg-ins|<id de la inserción>'.
 */
export interface AsignacionDelPlano {
  nivel: 'clase' | 'tipo' | 'pieza';
  clave: string;
  uso: string;
  /** DWG pieza: el archivo en el que vale. IFC y clase/tipo: null. */
  valeParaFileId: string | null;
  receta: Receta;
  memoriaId: string | null;
  memoriaVersion: number | null;
}

/** Las asignaciones del plano, ya filtradas por `filtrarAsignacionesDelPlano`. */
export type AsignacionesDelPlano = readonly AsignacionDelPlano[];

export type FuenteDeResolucion = 'pieza_plano' | 'tipo_plano' | 'tipo_memoria' | 'clase_plano' | 'motor';

export interface Resolucion {
  fuente: FuenteDeResolucion;
  /** null ⇔ fuente 'motor'. */
  receta: Receta | null;
  /** Solo con fuente 'tipo_memoria'. */
  memoria?: { id: string; version: number; deProyecto: string | null; fecha: string; porOtro: string | null };
  /** Piezas con excepción propia (agrupadas por receta idéntica), con sus gid, en el orden de `gidsDelTipo`. */
  excepciones: { gids: string[]; receta: Receta }[];
  /** Lo que hay en la memoria y NO se aplicó (para el aviso). null si se aplicó la memoria o decidió el plano por tipo. */
  pendiente: { estado: 'sugerencia' | 'conflicto' | 'no_vigente'; n: number } | null;
}

export interface EntradaResolverTipo {
  claveTipo: ClaveDeTipo | null;
  claveClase: ClaveDeTipo | null;
  /** IFC: GlobalId de las piezas del tipo. DWG: ids de las inserciones del bloque. */
  gidsDelTipo: string[];
  /** Las asignaciones del plano, ya filtradas por `vale_para_file_id`. */
  delPlano: AsignacionesDelPlano;
  /** Lo que devolvió /reconocer para este tipo (nivel tipo). null = sin consulta o sin memoria. */
  reconocimiento: ReconocimientoDeTipo | null;
}

// ---------------------------------------------------------------------------------------------------
// Cobertura de catálogos (§8)
// ---------------------------------------------------------------------------------------------------

export interface CoberturaDeCatalogo {
  catalogId: string;
  /** Tipos del plano con receta exacta y vigente ANCLADA en este catálogo (como si estuviera marcado). */
  reconoce: number;
  /** Tipos del plano con alguna sugerencia anclada en este catálogo. */
  sugerencias: number;
}

/** Un tipo del plano con lo que la memoria reconoce de él. `clave` null = el tipo no tiene clave (no cuenta en T). */
export interface TipoParaCobertura {
  clave: ClaveDeTipo | null;
  candidatas: readonly RecetaRecordada[];
}

export interface Cobertura {
  porCatalogo: CoberturaDeCatalogo[];
  /** T: tipos del plano CON clave. */
  total: number;
}

// ---------------------------------------------------------------------------------------------------
// Esquemas Zod (bordes de la API y lectura del borrador en la web)
// ---------------------------------------------------------------------------------------------------

const magnitudDelConceptoSchema = z
  .object({
    magnitud: z.enum(['conteo', 'lineal', 'area', 'volumen', 'peso', 'otro']),
    inputExtra: z.enum(PLANO_INPUT_EXTRA_VALORES),
  })
  .strict();

const derivacionSchema = z
  .object({
    derivaDe: z.enum(['largo', 'area', 'conteo', 'volumen', 'vanos']),
    factor: z.number().finite(),
    incluyeVanos: z.boolean().optional(),
    medidaM: z.number().finite().optional(),
    supuesto: z.boolean().optional(),
  })
  .strict();

export const lineaDeRecetaSchema: z.ZodType<LineaDeReceta> = z
  .object({
    orden: z.number().int().min(0),
    rol: z.enum(['principal', 'complemento']),
    deOrden: z.number().int().min(0).nullable(),
    catalogId: z.string().uuid(),
    clave: z.string().min(1).max(200),
    unidad: z.string().max(40).nullable(),
    descripcion: z.string().max(4000),
    magnitud: magnitudDelConceptoSchema.nullable(),
    derivacion: derivacionSchema.nullable(),
    supuesto: z.boolean(),
  })
  .strict();

/** Forma de una receta. Las reglas entre campos (§4 reglas 1-6, P4) las aplica `validarReceta` después. */
export const recetaSchema: z.ZodType<Receta> = z
  .object({
    noPresupuestar: z.boolean(),
    catalogoAncla: z.string().uuid().nullable(),
    recuerda: z.object({ magnitud: z.boolean(), partida: z.boolean(), complementarios: z.boolean() }).strict(),
    partida: z.string().max(300).nullable(),
    agrupacion: z
      .object({ especialidad: z.string(), faseObra: z.string(), ordenFase: z.number(), sistema: z.string() })
      .strict()
      .nullable(),
    lineas: z.array(lineaDeRecetaSchema).max(MAX_LINEAS_POR_RECETA),
  })
  .strict();

/** Forma de una clave que manda el navegador. La API además recalcula `canonica` con `canonicaDe` y rechaza si no coincide. */
export const claveDeTipoSchema: z.ZodType<ClaveDeTipo> = z
  .object({
    formato: z.enum(['ifc', 'dwg']),
    nivel: z.enum(['clase', 'tipo']),
    clase: z.string().min(1).max(200),
    nombre: z.string().max(MAX_LARGO_CANONICA),
    origenNombre: z.enum(['tipo', 'objectType', 'nombre', 'clase', 'capa', 'bloque']),
    uso: z.string().max(100),
    materiales: z.array(z.string().max(MAX_LARGO_MATERIAL_EN_CLAVE)).max(MAX_MATERIALES_EN_CLAVE),
    espesorMm: z.number().int().positive().nullable(),
    canonica: z.string().min(1).max(MAX_LARGO_CANONICA),
  })
  .strict();

// ---------------------------------------------------------------------------------------------------
// Funciones (§10)
// ---------------------------------------------------------------------------------------------------

const colapsar = (s: string): string => s.replace(/\s+/g, ' ').trim();

/**
 * La clase con la que se AGRUPA una pieza: las subclases «…StandardCase» / «…ElementedCase» de IFC2x3/IFC4 son el
 * mismo elemento que su clase madre (el esquema la dice, no se adivina). Misma regla que usa la web al medir.
 */
export function claseCanonica(clase: string, supertipoCercano: string | null | undefined): string {
  const m = /^(.*)(StandardCase|ElementedCase)$/.exec(clase);
  if (m && supertipoCercano && supertipoCercano.toUpperCase() === m[1]!.toUpperCase()) return supertipoCercano;
  return clase;
}

/** El nombre de una capa DWG sin el prefijo de referencia externa («ARQ|A-MUROS» → «A-MUROS»; anidadas también). */
function sinPrefijoXref(nombre: string): string {
  const i = nombre.lastIndexOf('|');
  return colapsar(i >= 0 ? nombre.slice(i + 1) : nombre) || colapsar(nombre);
}

/** El uso (PredefinedType) tal como lo declara el modelo; NOTDEFINED y USERDEFINED no dicen nada. */
function usoDeclarado(uso: string | null | undefined): string {
  const u = colapsar(uso ?? '');
  const mayus = u.toUpperCase();
  return mayus === 'NOTDEFINED' || mayus === 'USERDEFINED' ? '' : u;
}

function espesorValido(v: number | null | undefined): number | null {
  if (typeof v !== 'number' || !Number.isFinite(v)) return null;
  const r = Math.round(v);
  return r > 0 ? r : null;
}

/** Normalización tipográfica para comparar nombres: NFD → sin diacríticos → minúsculas → espacios colapsados → trim. */
export function normalizarNombreDeTipo(s: string): string {
  return colapsar(s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase());
}

/** La canónica de una clave (la misma en web y API): 'ifc|tipo|<clase>|<nombre norm>', 'ifc|clase|<clase>', 'dwg|capa|<norm sin «XREF|»>', 'dwg|bloque|<norm>'. */
export function canonicaDe(c: Omit<ClaveDeTipo, 'canonica'>): string {
  if (c.formato === 'ifc') {
    return c.nivel === 'clase' ? `ifc|clase|${c.clase}` : `ifc|tipo|${c.clase}|${normalizarNombreDeTipo(c.nombre)}`;
  }
  const nombre = c.clase === 'capa' ? sinPrefijoXref(c.nombre) : c.nombre;
  return `dwg|${c.clase}|${normalizarNombreDeTipo(nombre)}`;
}

function huellaDeMateriales(capa: PlanoCapa, deducidos: readonly { nombre: string; tipo: TipoMaterialIfc }[] | undefined): string[] {
  const desconocidos = new Set((deducidos ?? []).filter((d) => d.tipo === 'desconocido').map((d) => normalizarNombreDeTipo(d.nombre)));
  const huella = new Set<string>();
  for (const m of capa.ifc?.materiales ?? []) {
    const n = normalizarNombreDeTipo(m);
    if (n && !desconocidos.has(n)) huella.add(n.slice(0, MAX_LARGO_MATERIAL_EN_CLAVE));
  }
  return [...huella].sort().slice(0, MAX_MATERIALES_EN_CLAVE);
}

/** Clave de TIPO de una capa IFC; null si falta `tipoDelModelo` o `categoria`, o si el origen del nombre es 'clase'. */
export function claveDeTipoIfc(
  capa: PlanoCapa,
  materialesDeducidos?: readonly { nombre: string; tipo: TipoMaterialIfc }[],
): ClaveDeTipo | null {
  const t = capa.tipoDelModelo;
  if (!t || !capa.categoria || t.origen === 'clase') return null;
  const nombre = colapsar(t.nombre);
  if (!nombre) return null;
  const resto: Omit<ClaveDeTipo, 'canonica'> = {
    formato: 'ifc',
    nivel: 'tipo',
    clase: claseCanonica(capa.categoria, capa.ifc?.supertipos?.[0]),
    nombre,
    origenNombre: t.origen,
    uso: usoDeclarado(t.uso),
    materiales: huellaDeMateriales(capa, materialesDeducidos),
    espesorMm: espesorValido(t.espesorMm),
  };
  return { ...resto, canonica: canonicaDe(resto) };
}

/** Clave de CLASE de una capa IFC ('ifc|clase|<clase canónica>'); null si no trae `categoria`. */
export function claveDeClaseIfc(capa: PlanoCapa): ClaveDeTipo | null {
  if (!capa.categoria) return null;
  const resto: Omit<ClaveDeTipo, 'canonica'> = {
    formato: 'ifc',
    nivel: 'clase',
    clase: claseCanonica(capa.categoria, capa.ifc?.supertipos?.[0]),
    nombre: '',
    origenNombre: 'clase',
    uso: '',
    materiales: [],
    espesorMm: null,
  };
  return { ...resto, canonica: canonicaDe(resto) };
}

/** Clave de una capa DWG (quita el prefijo de referencia externa «XREF|», P5). */
export function claveDeCapaDwg(nombre: string): ClaveDeTipo {
  const resto: Omit<ClaveDeTipo, 'canonica'> = {
    formato: 'dwg',
    nivel: 'tipo',
    clase: 'capa',
    nombre: sinPrefijoXref(nombre),
    origenNombre: 'capa',
    uso: '',
    materiales: [],
    espesorMm: null,
  };
  return { ...resto, canonica: canonicaDe(resto) };
}

/** Clave de un bloque DWG (su `PlanoBloque.nombre`, que ya viene limpio). */
export function claveDeBloqueDwg(nombre: string): ClaveDeTipo {
  const resto: Omit<ClaveDeTipo, 'canonica'> = {
    formato: 'dwg',
    nivel: 'tipo',
    clase: 'bloque',
    nombre: colapsar(nombre),
    origenNombre: 'bloque',
    uso: '',
    materiales: [],
    espesorMm: null,
  };
  return { ...resto, canonica: canonicaDe(resto) };
}

const mismoConjunto = (a: readonly string[], b: readonly string[]): boolean => {
  const sa = new Set(a);
  const sb = new Set(b);
  return sa.size === sb.size && [...sa].every((x) => sb.has(x));
};

/** ¿La receta recordada vale para este tipo del plano? Tabla §3.3; uso o espesor distintos declarados por los dos → 'no' antes que cualquier sugerencia. */
export function coincidencia(plano: ClaveDeTipo, rec: ClaveRecordada): Coincidencia {
  // Dos tipos distintos no se sugieren entre sí.
  if (plano.uso && rec.uso && plano.uso !== rec.uso) return { tipo: 'no', motivo: null };
  if (plano.espesorMm != null && rec.espesorMm != null && plano.espesorMm !== rec.espesorMm) return { tipo: 'no', motivo: null };

  const sugerir = (motivo: MotivoDeSugerencia): Coincidencia => ({ tipo: 'sugerencia', motivo });
  if (plano.formato === 'dwg' || rec.formato === 'dwg') return sugerir('dwg');
  if (plano.nivel === 'clase' || rec.nivel === 'clase') return sugerir('clase');
  if (rec.soloSugerir) return sugerir('solo_sugerir');
  const nombreDeTipo = (o: OrigenDelNombre): boolean => o === 'tipo' || o === 'objectType';
  if (!nombreDeTipo(plano.origenNombre) || !nombreDeTipo(rec.origenNombre)) return sugerir('nombre_de_pieza');
  if ((plano.uso === '') !== (rec.uso === '')) return sugerir('uso_no_dicho');
  if (plano.materiales.length > 0 && rec.materiales.length > 0) {
    if (!mismoConjunto(plano.materiales, rec.materiales)) return sugerir('material_distinto');
  } else if (plano.materiales.length !== rec.materiales.length) {
    return sugerir('material_no_dicho');
  }
  return { tipo: 'exacta', motivo: null };
}

const aComparar = (s: string): string => colapsar(s.normalize('NFC')).toLowerCase();

/** Misma descripción de catálogo: NFC, espacios colapsados, trim, sin distinguir mayúsculas (nada más). */
export function mismaDescripcion(a: string, b: string): boolean {
  return aComparar(a) === aComparar(b);
}

/** Estado de una línea recordada contra el catálogo actual, en el orden de la tabla §7.1 (la unidad se compara exacta). */
export function estadoDeLinea(
  foto: { unidad: string | null; descripcion: string },
  actual: { unidad: string | null; descripcion: string; precio: number | null } | null,
  cat: { existe: boolean; marcado: boolean },
): EstadoDeLinea {
  if (!cat.existe) return 'catalogo_no_disponible';
  if (!cat.marcado) return 'catalogo_no_marcado';
  if (!actual) return 'no_existe';
  if (foto.unidad !== actual.unidad) return 'cambio_unidad';
  if (!mismaDescripcion(foto.descripcion, actual.descripcion)) return 'cambio_descripcion';
  if (!(typeof actual.precio === 'number' && actual.precio > 0)) return 'sin_precio';
  return 'vigente';
}

/** Una receta es vigente ⇔ todas sus líneas están 'vigente' (sin líneas, noPresupuestar: vigente). */
export function recetaVigente(estados: readonly EstadoDeLinea[]): boolean {
  return estados.every((e) => e === 'vigente');
}

/** Reglas entre campos de una receta (§4 reglas 1-6, un solo principal, P4 sin partida, orden 0..n-1). */
export function validarReceta(r: Receta): ValidacionReceta {
  const no = (motivo: string): ValidacionReceta => ({ ok: false, motivo });
  if (r.partida !== null || r.recuerda.partida) return no('La receta no guarda partida.');
  if (r.lineas.length > MAX_LINEAS_POR_RECETA) return no(`Una receta lleva como máximo ${MAX_LINEAS_POR_RECETA} líneas.`);

  if (r.noPresupuestar) {
    if (r.lineas.length > 0 || r.catalogoAncla !== null) return no('«No presupuestar» no lleva conceptos ni catálogo.');
    return { ok: true };
  }
  if (r.lineas.length === 0 || r.catalogoAncla === null) return no('La receta necesita al menos un concepto principal.');

  for (let i = 0; i < r.lineas.length; i++) {
    if (r.lineas[i]!.orden !== i) return no('El orden de las líneas debe ser 0, 1, 2… sin repetir ni saltar.');
  }
  const principales = r.lineas.filter((l) => l.rol === 'principal');
  if (principales.length === 0) return no('La receta necesita al menos un concepto principal.');
  if (principales.length > MAX_PRINCIPALES_POR_RECETA) return no('Una receta lleva un solo concepto principal; lo demás son complementarios.');
  const principal = principales[0]!;
  if (r.catalogoAncla !== principal.catalogId) return no('El catálogo de la receta debe ser el del concepto principal.');

  for (const l of r.lineas) {
    if (l.rol === 'principal') {
      if (l.deOrden !== null) return no('El concepto principal no cuelga de otro.');
    } else {
      const padre = l.deOrden === null ? undefined : r.lineas[l.deOrden];
      if (!padre || padre.rol !== 'principal') return no('Cada complementario debe colgar de un concepto principal.');
    }
  }
  const hayComplementos = r.lineas.some((l) => l.rol === 'complemento');
  if (!r.recuerda.complementarios && hayComplementos) return no('Si no se recuerdan los complementarios, la receta no puede llevarlos.');
  if (!r.recuerda.magnitud && r.lineas.some((l) => l.magnitud !== null)) return no('Si no se recuerda qué se mide, la receta no puede traerlo.');
  return { ok: true };
}

const tieneCatalogo = (r: PlanoRenglon): boolean => !!r.catalogId && !!r.resourceClave;

/** La derivación de ESTE elemento (solo los campos que la receta recuerda) en la foto de un complementario. */
function derivacionDelElemento(r: PlanoRenglon, etiquetas: ReadonlySet<string>): DerivacionDeComplementario | null {
  const linea = r.generador?.lineas.find((l) => l.elemento && etiquetas.has(l.elemento.clave) && l.derivacion);
  const d = linea?.derivacion;
  if (!d) return null;
  return {
    derivaDe: d.derivaDe,
    factor: d.factor,
    ...(d.incluyeVanos !== undefined ? { incluyeVanos: d.incluyeVanos } : {}),
    ...(d.medidaM !== undefined ? { medidaM: d.medidaM } : {}),
    ...(d.supuesto !== undefined ? { supuesto: d.supuesto } : {}),
  };
}

/** La receta de un elemento a partir de los renglones de la hoja cuya selección (o `ademas`) es una de sus etiquetas. */
export function recetaDesdeLaHoja(renglones: readonly PlanoRenglon[], etiquetas: readonly string[]): RecetaDesdeLaHoja {
  const mias = new Set(etiquetas);
  const principalesDelElemento = renglones.filter((r) => r.rol !== 'complemento' && seleccionesDe(r).some((s) => mias.has(s.clave)));
  const idsDelElemento = new Set(principalesDelElemento.map((r) => r.id));
  const cuelgaDe = (c: PlanoRenglon, ids: ReadonlySet<string>): boolean =>
    (c.principalId != null && ids.has(c.principalId)) || (c.padres ?? []).some((p) => ids.has(p));
  const complementosDelElemento = renglones.filter((r) => r.rol === 'complemento' && cuelgaDe(r, idsDelElemento));

  const sinCatalogo = [...principalesDelElemento, ...complementosDelElemento].filter((r) => !tieneCatalogo(r)).map((r) => r.conceptoDescripcion);
  const conCatalogo = principalesDelElemento.filter(tieneCatalogo);
  const elegido = conCatalogo[0];
  const otrosPrincipales = conCatalogo.slice(1).map((r) => r.conceptoDescripcion);
  if (!elegido) return { receta: null, sinCatalogo, otrosPrincipales };

  const lineas: LineaDeReceta[] = [
    {
      orden: 0,
      rol: 'principal',
      deOrden: null,
      catalogId: elegido.catalogId!,
      clave: elegido.resourceClave!,
      unidad: elegido.unidad,
      descripcion: elegido.conceptoDescripcion,
      magnitud: elegido.magnitudConcepto ? { magnitud: elegido.magnitudConcepto.magnitud, inputExtra: elegido.magnitudConcepto.inputExtra } : null,
      derivacion: null,
      supuesto: false,
    },
  ];
  const suyos = new Set([elegido.id]);
  for (const c of complementosDelElemento.filter((x) => tieneCatalogo(x) && cuelgaDe(x, suyos))) {
    const derivacion = derivacionDelElemento(c, mias);
    lineas.push({
      orden: lineas.length,
      rol: 'complemento',
      deOrden: 0,
      catalogId: c.catalogId!,
      clave: c.resourceClave!,
      unidad: c.unidad,
      descripcion: c.conceptoDescripcion,
      magnitud: null,
      derivacion,
      supuesto: c.revisar || derivacion?.supuesto === true,
    });
  }
  const a = elegido.agrupacion;
  return {
    receta: {
      noPresupuestar: false,
      catalogoAncla: elegido.catalogId,
      recuerda: { magnitud: true, partida: false, complementarios: true },
      partida: null,
      agrupacion: a ? { especialidad: a.especialidad, faseObra: a.faseObra, ordenFase: a.ordenFase, sistema: a.sistema } : null,
      lineas,
    },
    sinCatalogo,
    otrosPrincipales,
  };
}

/** Separa las asignaciones de pieza DWG de otro archivo (`valeParaFileId` ≠ actual) para no aplicarlas. */
export function filtrarAsignacionesDelPlano(
  asignaciones: readonly AsignacionDelPlano[],
  fileIdActual: string,
): { activas: AsignacionDelPlano[]; inactivas: AsignacionDelPlano[] } {
  const activas: AsignacionDelPlano[] = [];
  const inactivas: AsignacionDelPlano[] = [];
  for (const a of asignaciones) {
    (a.nivel === 'pieza' && a.valeParaFileId !== null && a.valeParaFileId !== fileIdActual ? inactivas : activas).push(a);
  }
  return { activas, inactivas };
}

const vigenteLaCandidata = (c: RecetaRecordada): boolean => recetaVigente(c.lineas.map((l) => l.estado));
/** Una clase recordada nunca es exacta (D4): solo el nivel tipo puede aplicarse sola. */
const exactaDeTipo = (c: RecetaRecordada): boolean => c.coincidencia === 'exacta' && c.nivel === 'tipo';

/** Estado de un tipo según sus candidatas (el que manda la API y el que usa `resolverTipo`): §6.1 paso 3 y §7.2. */
export function estadoDeReconocimiento(candidatas: readonly RecetaRecordada[]): EstadoDeReconocimiento {
  if (candidatas.length === 0) return 'sin_memoria';
  const exactas = candidatas.filter(exactaDeTipo);
  const vigentes = exactas.filter(vigenteLaCandidata);
  if (vigentes.length === 1) return 'exacta';
  if (vigentes.length > 1) return 'conflicto';
  if (exactas.length > 0) return 'no_vigente';
  return 'sugerencia';
}

/** Precedencia por elemento: pieza del plano → tipo del plano → memoria exacta y vigente única → clase del plano → motor. */
export function resolverTipo(e: EntradaResolverTipo): Resolucion {
  // 1. Piezas con excepción propia: salen del tipo, agrupadas por receta idéntica, en el orden de `gidsDelTipo`.
  const porClave = new Map(e.delPlano.filter((a) => a.nivel === 'pieza').map((a) => [a.clave, a] as const));
  const grupos = new Map<string, { gids: string[]; receta: Receta }>();
  for (const gid of e.gidsDelTipo) {
    const a = porClave.get(`ifc-gid|${gid}`) ?? porClave.get(`dwg-ins|${gid}`);
    if (!a) continue;
    const llave = JSON.stringify(a.receta);
    const g = grupos.get(llave);
    if (g) g.gids.push(gid);
    else grupos.set(llave, { gids: [gid], receta: a.receta });
  }
  const excepciones = [...grupos.values()];

  // 2. El tipo decidido en este plano.
  const k = e.claveTipo;
  const tipoPlano = k ? e.delPlano.find((a) => a.nivel === 'tipo' && a.clave === k.canonica && a.uso === k.uso) : undefined;
  if (tipoPlano) return { fuente: 'tipo_plano', receta: tipoPlano.receta, excepciones, pendiente: null };

  // 3. La memoria, solo si hay UNA exacta y vigente.
  const candidatas = e.reconocimiento?.candidatas ?? [];
  const estado = estadoDeReconocimiento(candidatas);
  if (estado === 'exacta') {
    const c = candidatas.filter(exactaDeTipo).find(vigenteLaCandidata)!;
    return {
      fuente: 'tipo_memoria',
      receta: c.receta,
      memoria: { id: c.memoriaId, version: c.version, deProyecto: c.origen.proyecto, fecha: c.origen.fecha, porOtro: c.origen.porOtraPersona },
      excepciones,
      pendiente: null,
    };
  }

  // 4. Lo que quedó sin aplicarse de la memoria, para el aviso.
  let pendiente: Resolucion['pendiente'] = null;
  if (estado === 'conflicto') pendiente = { estado, n: candidatas.filter(exactaDeTipo).filter(vigenteLaCandidata).length };
  else if (estado === 'no_vigente') pendiente = { estado, n: candidatas.filter(exactaDeTipo).length };
  else if (estado === 'sugerencia') pendiente = { estado, n: candidatas.length };

  // 5. La clase decidida en este plano, y si no, el motor de siempre.
  const c = e.claveClase;
  const clasePlano = c ? e.delPlano.find((a) => a.nivel === 'clase' && a.clave === c.canonica) : undefined;
  if (clasePlano) return { fuente: 'clase_plano', receta: clasePlano.receta, excepciones, pendiente };
  return { fuente: 'motor', receta: null, excepciones, pendiente };
}

/** «Reconoce N de T» por catálogo: cuenta por ancla como si estuviera marcado; sugerencias aparte. */
export function cobertura(tipos: readonly TipoParaCobertura[], catalogos: readonly string[]): Cobertura {
  const conClave = tipos.filter((t) => t.clave !== null);
  // «Como si el catálogo estuviera marcado»: lo único que no cuenta es lo que ya no existe o cambió.
  const vigenteSiMarcado = (c: RecetaRecordada): boolean => c.lineas.every((l) => l.estado === 'vigente' || l.estado === 'catalogo_no_marcado');
  const porCatalogo = catalogos.map((catalogId) => {
    let reconoce = 0;
    let sugerencias = 0;
    for (const t of conClave) {
      const anclada = t.candidatas.filter((c) => c.receta.catalogoAncla === catalogId);
      if (anclada.some((c) => exactaDeTipo(c) && vigenteSiMarcado(c))) reconoce++;
      else if (anclada.some((c) => c.coincidencia === 'sugerencia' || c.nivel === 'clase')) sugerencias++;
    }
    return { catalogId, reconoce, sugerencias };
  });
  return { porCatalogo, total: conClave.length };
}

/** Catálogo a pre-marcar: el de mayor `reconoce`; empate o todos 0 → el propuesto hoy; empate sin él → el primero de `orden`. */
export function catalogoAPreMarcar(
  porCatalogo: readonly CoberturaDeCatalogo[],
  propuestoHoy: string | null,
  orden: readonly string[],
): string | null {
  const max = porCatalogo.reduce((m, c) => Math.max(m, c.reconoce), 0);
  if (max === 0) return propuestoHoy;
  const empatados = porCatalogo.filter((c) => c.reconoce === max).map((c) => c.catalogId);
  if (empatados.length === 1) return empatados[0]!;
  if (propuestoHoy && empatados.includes(propuestoHoy)) return propuestoHoy;
  return orden.find((id) => empatados.includes(id)) ?? empatados[0]!;
}
